'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Bell, CalendarCheck, Clock, Play, Smartphone, Store } from 'lucide-react';
import { publicApi } from '@/lib/api';
import { toneChip } from '@/lib/tones';

type AppItem = {
  name: string;
  role: string;
  tier: React.ReactNode;
  lead: string;
  features: Array<{ icon: React.ReactNode; label: string }>;
};

/**
 * Vitrine dos apps mobile na pagina /pricing. Mesma anatomia dos cards de
 * plano (.section-card + .pricing-card) para coesao: ícone-título via toneChip
 * (padrão do DS) + nome centrado, e um CTA de loja (placeholder enquanto a URL
 * real não é publicada - vem do RuntimeConfig via getMobileLanding).
 */
export function MobileApps() {
  const [androidUrl, setAndroidUrl] = useState<string>('');

  useEffect(() => {
    publicApi
      .getMobileLanding()
      .then((r) => setAndroidUrl(r.data?.androidUrl ?? ''))
      .catch(() => {
        /* sem link enquanto nao publicado - botao vira placeholder */
      });
  }, []);

  const apps: AppItem[] = [
    {
      name: '7barber',
      role: 'App do cliente',
      tier: <Smartphone className="w-5 h-5" aria-hidden="true" />,
      lead: 'Seus clientes agendam, acompanham os horários e recebem os avisos da barbearia direto no celular.',
      features: [
        { icon: <CalendarCheck className="pricing-feature__icon" aria-hidden="true" />, label: 'Agendamento e acompanhamento dos horários' },
        { icon: <Bell className="pricing-feature__icon" aria-hidden="true" />, label: 'Avisos e comunicações da barbearia' },
      ],
    },
    {
      name: '7barber Gestão',
      role: 'App do dono',
      tier: <Store className="w-5 h-5" aria-hidden="true" />,
      lead: 'Gerencie agenda, equipe e financeiro da sua barbearia de onde estiver, direto pelo celular.',
      features: [
        { icon: <CalendarCheck className="pricing-feature__icon" aria-hidden="true" />, label: 'Agenda e atendimentos na palma da mão' },
        { icon: <Store className="pricing-feature__icon" aria-hidden="true" />, label: 'Gestão da equipe e do financeiro' },
      ],
    },
  ];

  return (
    <section className="pricing-mobile-section">
      <div className="pricing-mobile-head">
        <span className="pricing-hero__eyebrow">
          <Smartphone className="w-3.5 h-3.5" aria-hidden="true" />
          Também no celular
        </span>
        <h2 className="pricing-section__title">Sua barbearia no bolso</h2>
        <p className="pricing-mobile-head__lead">
          Apps nativos já disponíveis no Android (Play Store) - para você e para
          os seus clientes. Versão iOS em breve.
        </p>
      </div>

      <div className="pricing-appgrid">
        {apps.map((app, i) => {
          // Hoje só o app do cliente tem URL publicada; o do dono fica
          // placeholder ('#') ate a loja sair. Depois ligamos a URL real.
          const storeUrl = i === 0 && androidUrl ? androidUrl : '#';
          const isPlaceholder = storeUrl === '#';
          return (
            <div key={app.name} className="section-card pricing-card">
              <div className="pricing-card__head">
                <span className={`pricing-card__tier ${toneChip('muted')}`}>{app.tier}</span>
                <h3 className="pricing-card__name">{app.name}</h3>
              </div>
              <span className="pricing-applabel">{app.role}</span>
              <p className="pricing-applead">{app.lead}</p>

              <div className="pricing-card__divider" />

              <ul className="pricing-features">
                {app.features.map((f) => (
                  <li key={f.label} className="pricing-feature">
                    {f.icon}
                    <span className="pricing-feature__text">{f.label}</span>
                  </li>
                ))}
              </ul>

              <div className="pricing-appstores">
                <Link
                  href={storeUrl}
                  className="btn btn--sm btn--primary"
                  {...(isPlaceholder
                    ? { 'aria-disabled': true, title: 'Em breve na Google Play' }
                    : { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  <Play aria-hidden="true" />
                  <span>Baixar no Google Play</span>
                </Link>
                <span className="pricing-store-soon">
                  <Clock className="w-3.5 h-3.5" aria-hidden="true" /> iOS em breve
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
